import { useEffect, useMemo, useRef } from 'react';
import { Animated, Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { ErrorText } from '@/components/ErrorText';

type TagOption = { key: string; label: string };

type TagsInput = {
  options: TagOption[];
  selected: string[];
  onToggle: (key: string) => void;
  label?: string;
};

type Props = {
  visible: boolean;
  title: string;
  message?: string;
  bullets?: string[];
  warning?: string;
  confirmLabel: string;
  cancelLabel?: string | undefined;
  danger?: boolean;
  loading?: boolean;
  error?: string | null;
  tags?: TagsInput;
  onConfirm: () => void;
  onCancel: () => void;
  /** Προαιρετικό 3ο, λιγότερο εμφανές κουμπί (π.χ. "Απόρριψη" σε
   * "αποθήκευση ως πρόχειρο ή απόρριψη" prompts) — εμφανίζεται πάνω από τα
   * δύο κύρια κουμπιά. */
  extraAction?: { label: string; onPress: () => void; danger?: boolean };
};

export function ConfirmModal({
  visible,
  title,
  message,
  bullets,
  warning,
  confirmLabel,
  cancelLabel,
  danger = false,
  loading = false,
  error,
  tags,
  onConfirm,
  onCancel,
  extraAction,
}: Props) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const resolvedCancelLabel = cancelLabel ?? t('confirmModal.cancelDefault');

  const scale = useRef(new Animated.Value(0.9)).current;
  const opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!visible) return;
    scale.setValue(0.9);
    opacity.setValue(0);
    Animated.parallel([
      Animated.spring(scale, { toValue: 1, useNativeDriver: Platform.OS !== 'web', friction: 7, tension: 90 }),
      Animated.timing(opacity, { toValue: 1, duration: 160, useNativeDriver: Platform.OS !== 'web' }),
    ]).start();
  }, [visible, scale, opacity]);

  if (!visible) return null;

  return (
    <Modal transparent visible animationType="fade" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <Animated.View style={[styles.card, { opacity, transform: [{ scale }] }]}>
          <Text style={styles.title}>{title}</Text>
          {!!message && <Text style={styles.message}>{message}</Text>}

          {!!warning && (
            <View style={styles.warningBox}>
              <Text style={styles.warningIcon}>⚠️</Text>
              <Text style={styles.warningText}>{warning}</Text>
            </View>
          )}

          {!!bullets?.length && (
            <View style={styles.bullets}>
              {bullets.map((b, i) => (
                <Text key={i} style={styles.bullet}>• {b}</Text>
              ))}
            </View>
          )}

          {tags && (
            <View style={styles.tagsSection}>
              <Text style={styles.tagsLabel}>{tags.label ?? t('confirmModal.tagsLabelDefault')}</Text>
              <View style={styles.tagsList}>
                {tags.options.map((opt) => {
                  const selected = tags.selected.includes(opt.key);
                  return (
                    <Pressable
                      key={opt.key}
                      style={[styles.tag, selected && styles.tagSelected]}
                      onPress={() => tags.onToggle(opt.key)}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: selected }}
                    >
                      <Text style={[styles.tagText, selected && styles.tagTextSelected]}>
                        {opt.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            </View>
          )}

          <ErrorText>{error}</ErrorText>

          {!!extraAction && (
            <Pressable onPress={extraAction.onPress} disabled={loading} hitSlop={8} style={styles.extraActionBtn}>
              <Text style={[styles.extraActionText, extraAction.danger && styles.extraActionTextDanger]}>
                {extraAction.label}
              </Text>
            </Pressable>
          )}

          <View style={styles.actions}>
            <Button title={confirmLabel} onPress={onConfirm} loading={loading} danger={danger} />
            <Button title={resolvedCancelLabel} variant="secondary" onPress={onCancel} disabled={loading} />
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    overlay: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.6)',
      padding: spacing.lg,
    },
    card: {
      width: '100%',
      maxWidth: 420,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.lg,
      gap: spacing.md,
    },
    title: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '700',
    },
    message: {
      color: colors.text,
      fontSize: fontSize.md,
      lineHeight: 22,
    },
    bullets: {
      gap: spacing.xs,
    },
    bullet: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      lineHeight: 21,
    },
    tagsSection: {
      gap: spacing.sm,
    },
    tagsLabel: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    tagsList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.sm,
    },
    tag: {
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.border,
      paddingVertical: 6,
      paddingHorizontal: spacing.md,
      backgroundColor: colors.surfaceAlt,
    },
    tagSelected: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    tagText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    tagTextSelected: {
      color: colors.textOnPrimary,
    },
    warningBox: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      backgroundColor: 'rgba(251,191,36,0.12)',
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: 'rgba(251,191,36,0.40)',
      padding: spacing.md,
    },
    warningIcon: {
      fontSize: 16,
      lineHeight: 22,
    },
    warningText: {
      flex: 1,
      color: '#FBBF24',
      fontSize: fontSize.sm,
      fontWeight: '700',
      lineHeight: 20,
    },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
    actions: {
      gap: spacing.sm,
      marginTop: spacing.xs,
    },
    extraActionBtn: {
      alignSelf: 'center',
      paddingVertical: spacing.xs,
    },
    extraActionText: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    extraActionTextDanger: {
      color: colors.danger,
    },
  });
}
